import { useConfig } from '../providers/ConfigProvider.js';
import { useErrorContext } from '../providers/ErrorContextProvider.js';
import { usePayPalButtonContext } from '../providers/PayPalButtonContextProvider.js';
import AdobeTrackingService from '../services/AdobeTrackingService.js';
import PayPalPaymentService from '../services/PayPalPaymentService.js';
import { captureException } from '../services/sentryService.js';
import { TrackingIdentifier } from '../types-internal/AdobeTracking.js';
import { convertZppLocaleToPayPalLocale } from '../utils/PayPalUtils.js';
import { useTranslation } from './useTranslations.js';

const usePaypalButtonState = ({ amountMajorUnit, currency, locale, track, }) => {
    const { t } = useTranslation();
    const { setError } = useErrorContext();
    const { config: { paypal: { clientId }, }, } = useConfig();
    const { userIdToken } = usePayPalButtonContext();
    const initiatePayPalOptions = {
        clientId,
        currency: currency,
        locale: convertZppLocaleToPayPalLocale(locale),
        dataUserIdToken: userIdToken,
        intent: 'authorize',
        components: ['buttons', 'funding-eligibility'],
        disableFunding: ['card', 'sepa'], // follow shop-checkout
    };
    const waitAndTrackModalDisplayed = () => {
        // Wait 2 seconds, then check and send tracking events for the modal displayed event
        setTimeout(() => {
            const paypalSandbox = document.getElementsByClassName('paypal-checkout-sandbox');
            if (track) {
                paypalSandbox.length === 1
                    ? track('PaypalModalDisplayed')
                    : track('PaypalModalNotDisplayed');
            }
            else {
                paypalSandbox.length === 1
                    ? AdobeTrackingService.commonTracking('PaypalModalDisplayed')
                    : AdobeTrackingService.commonTracking('PaypalModalNotDisplayed');
            }
        }, 2000);
    };
    const createOrder = async () => {
        waitAndTrackModalDisplayed();
        try {
            return await PayPalPaymentService.createOrder(amountMajorUnit);
        }
        catch (error) {
            /* An error message should always be shown whether the cart is conflicted
             * or other error happened. */
            setError({
                message: t('checkout.payment.paypal.error.order.create'),
            });
            /* The HTTP status 409_CONFLICT means the page is stale, cart has been modified.
             * This error is likely to be handled without Sentry capturing. */
            if (error.isAxiosError && error.response?.status === 409) {
                // Refresh page + print cart changed
                location.href = '/checkout/preview?paypalConsistencyCheck=true';
            }
            else {
                captureException({ message: 'Error while creating checkout PayPal order', error });
            }
        }
        return '';
    };
    const onBuyNowButtonClicked = () => {
        AdobeTrackingService.checkoutTracking(TrackingIdentifier.PAYPAL_PREVIEW_BUTTON, 'Buy Now');
    };
    const onError = (error) => {
        captureException({ message: 'Error while processing PayPal payment', error });
        setError({
            message: t('checkout.payment.paypal.error.general'),
        });
    };
    return {
        initiatePayPalOptions,
        createOrder,
        onBuyNowButtonClicked,
        onError,
    };
};

export { usePaypalButtonState };
//# sourceMappingURL=usePaypalButtonState.js.map
